import { useState, useEffect, useCallback, useRef } from 'react';
import videojs from "video.js";
import moment from 'moment';
import { useDispatch } from "react-redux";
import {
  getTokenOneTime,
  getTokenOneTimeResult
} from "Redux/actions";
import { endpoints } from 'Core/defaultValues';
import { NotificationManager } from "Components/Notifications";

export const useInput = (initialValue, label = '', placeholder = '') => {
  const [value, setValue] = useState(initialValue);
  useEffect(
    () => {
      setValue(initialValue);
    },
    [initialValue]
  );
  const onChange = (txt) => {
    setValue(txt);
  };
  return {
    value,
    onChange,
    label,
    placeholder
  };
};

export const useTimePicker = () => {
  const ampmOptions = [{ 'id': 1, 'name': 'AM' }, { 'id': 2, 'name': 'PM' }];
  const now = moment();
  const hour = useInput();
  const minute = useInput();
  const second = useInput();
  const [ampm, setAmPm] = useState(JSON.parse(JSON.stringify(now.hour() > 12 ? ampmOptions[1] : ampmOptions[0])));

  useEffect(() => {
    // Default value for time input
    const defaultHour = now.hour() > 12 ? now.hour() - 12 : now.hour()
    hour.onChange(defaultHour);
    minute.onChange(now.minutes().toString());
    second.onChange(now.seconds().toString());
  }, []);

  const handleChange = (value, type) => {
    if (type === 'am/pm') {
      setAmPm(value);
      return getCompleteHour(null, null, null, value);
    } else if (type === 'hour') {
      hour.onChange(value);
      return getCompleteHour(value, null, null, null);
    } else if (type === 'minute') {
      minute.onChange(value);
      return getCompleteHour(null, value, null, null);
    } else if (type === 'second') {
      second.onChange(value);
      return getCompleteHour(null, null, value, null);
    }

  }

  const setHour = (newHour, newMinute, newSecond, newAmPm) => {
    const now = moment();
    const defaultHour = now.hour() > 12 ? now.hour() - 12 : now.hour()

    hour.onChange(newHour || defaultHour);
    minute.onChange(newMinute || now.minutes());
    second.onChange(newSecond || now.seconds());

    if (newAmPm) {
      const findAmPm = ampmOptions.find(ampm => ampm.name === newAmPm);
      setAmPm(findAmPm);
    } else {
      setAmPm(ampmOptions[0]);
    }
  }

  const getCompleteHour = (tempHour, tempMinute, tempSecond, tempAmpm) => {
    let newTempAmpm = tempAmpm;
    if (typeof tempAmpm === 'string') {
      newTempAmpm = {
        name: tempAmpm
      }
    }
    return `${formatNumberToTwoDigists(tempHour || hour.value)}:${formatNumberToTwoDigists(tempMinute || minute.value)}:${formatNumberToTwoDigists(tempSecond || second.value)} ${newTempAmpm?.name || (ampm?.name || '')}`;
  }

  const formatNumberToTwoDigists = (number) => {
    if (!number) {
      return '00';
    }

    let newNumber = number;
    if (number.length === 0) {
      newNumber = '00';
      return newNumber;
    }

    if (number < 10 && String(number || '').length <= 1) {
      newNumber = `0${number}`;
      return newNumber;
    }

    return newNumber;
  }

  return {
    hour,
    minute,
    second,
    ampm,
    handleChange,
    getCompleteHour,
    formatNumberToTwoDigists,
    setHour
  }
};

export const useDetectOutsideClick = (ref) => {
  const [clickInside, setClickInside] = useState(false);
  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target) && typeof event.target.className === 'string' && !event.target.className?.includes("react-select__option")) {
        setClickInside(false);
      } else {
        setClickInside(true);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref]);

  const onChange = (value) => {
    setClickInside(value);
  }

  return {
    clickInside,
    onChange
  };
}

export const useTokenOneTimeHandler = (tokenOneTime, dealers) => {
  const dispatch = useDispatch();
  const [openUrlLocal, setOpenUrlLocal] = useState(false);
  const [copyUrl, setCopyUrl] = useState(false);
  const [dealerId, setDealerId] = useState(null);
  async function copyLinkToClipboard(url) {
    try {
      await navigator.clipboard.writeText(url);
    } catch (err) {
      NotificationManager.error('Error', 'Failed to copy link to clipboard.', 3000, null, null, '', null);
    }
  }
  // Effect to handle tokenOneTime changes
  useEffect(() => {
    if (tokenOneTime?.id) {
      let endpoint = endpoints.CLOUD_WEBSITE;

      if (openUrlLocal) {
        endpoint = 'http://localhost:40000';
        window.open(`${endpoint}/login/pm?&token=${tokenOneTime.id}`, '_blank');
      } else {
        if (endpoints.ENVIRONMENT === "production") {
          let dealer = dealers.find(dealer => dealer?.id === dealerId);
          if (dealer?.brand?.domains?.length > 0) {
            try {
              let domains = JSON.parse(JSON.parse(dealer?.brand?.domains));
              if (domains?.length > 0 && domains[0]?.length > 0) {
                endpoint = domains[0];
              }
            } catch (exc) {
              console.error("Error parsing dealer domains:", exc);
            }
          }
        }
        let url = `${endpoint.startsWith('https://') ? '' : 'https://'}${endpoint}/login/pm?token=${tokenOneTime.id}`;
        if (copyUrl) {
          copyLinkToClipboard(url);
          NotificationManager.success('Success', 'Link copied to clipboard.', 3000, null, null, '', null);
        } else {
          window.open(url, '_blank');
        }
      }

      dispatch(getTokenOneTimeResult(null));
      setOpenUrlLocal(false);
      setDealerId(null);
      setCopyUrl(null)
    }
  }, [tokenOneTime?.id]);

  // Function to handle authentication
  const handleAuthenticate = (e, dealerId, username, authId) => {
    e?.preventDefault();

    setDealerId(dealerId || null);
    // Copy URL if Alt key is pressed to clipboard
    setCopyUrl(!!e?.altKey);
    // Open URL in local environment if Shift key is pressed
    setOpenUrlLocal(!!e?.shiftKey);

    dispatch(getTokenOneTime({
      username,
      token: localStorage.getItem("IdToken"),
      authId
    }));
  };

  return { handleAuthenticate };
};

export default useTokenOneTimeHandler;

/**
 * Hook para agregar y remover botones personalizados en Video.js
 */
export const useVideojsButton = (videoPlayer, props) => {
  /**
   * ✅ Agrega un botón personalizado al control bar
   */
  const addCustomButton = useCallback(
    ({ name, label, iconClass, position, onClick }) => {
      const BaseButton = videojs.getComponent("Button");

      if (!videojs.getComponent(name)) {
        class CustomButton extends BaseButton {
          constructor(player, options) {
            super(player, options);
            this.applyConfig(options);
          }

          applyConfig(options = {}) {
            if (options.label) {
              this.controlText(options.label);
            }

            if (options.iconClass) {
              this.addClass(options.iconClass);
            }

            this.options_ = {
              ...this.options_,
              onClick: options.onClick,
            };
          }

          handleClick() {
            const handler = this.options_?.onClick;
            if (handler) {
              handler();
            }
          }
        }

        videojs.registerComponent(name, CustomButton);
      }

      const controlBar = videoPlayer[props.id]?.getChild("controlBar");
      if (!controlBar) {
        return;
      }

      const config = { label, iconClass, onClick };
      let buttonInstance = controlBar.getChild(name);

      if (!buttonInstance) {
        controlBar.addChild(name, config, position);
        buttonInstance = controlBar.getChild(name);
      }

      buttonInstance?.applyConfig?.(config);
      if (buttonInstance && !buttonInstance.applyConfig) {
        if (label) {
          buttonInstance.controlText(label);
        }
        if (iconClass) {
          buttonInstance.addClass(iconClass);
        }
        buttonInstance.options_ = {
          ...buttonInstance.options_,
          onClick,
        };
      }
    },
    [props.id, videoPlayer]
  );

  /**
   * 🧹 Remueve un botón del control bar (si existe)
   */
  const removeCustomButton = useCallback(
    (name) => {
      const controlBar = videoPlayer[props.id]?.getChild("controlBar");
      const existingButton = controlBar?.getChild(name);
      if (existingButton) {
        controlBar.removeChild(existingButton);
        existingButton.dispose?.(); // Limpia recursos
      }
    },
    [props.id, videoPlayer]
  );

  return { addCustomButton, removeCustomButton };
};